import axios from 'axios'
import { Article, FilterState, DashboardStats } from '../types'

// Smart API Configuration - Environment aware
const getApiBaseUrl = () => {
  // 1. Explicit environment variable override
  if (process.env.REACT_APP_API_URL) {
    return process.env.REACT_APP_API_URL
  }
  
  // 2. Production detection
  if (process.env.NODE_ENV === 'production') {
    // In production, API is served by same server via reverse proxy
    return '/api/v1'
  }
  
  // 3. Development - check if we're running on different ports
  const currentHost = window.location.hostname
  const currentPort = window.location.port
  
  // If React app is on port 3000, backend is likely on 8002
  if (currentPort === '3000') {
    return `http://${currentHost}:8002/api/v1`
  }
  
  // Fallback to localhost
  return 'http://localhost:8002/api/v1'
}

const API_BASE_URL = getApiBaseUrl()

// Debug logging in development
if (process.env.NODE_ENV === 'development') {
  console.log('🔌 API Base URL:', API_BASE_URL)
}

const api = axios.create({
  baseURL: API_BASE_URL,
  timeout: 30000,
  withCredentials: true, // Include cookies for authentication
  headers: {
    'Content-Type': 'application/json',
  },
})

// Commodity mapping - EXACT MiningHub backend codes only
const COMMODITY_MAPPING: Record<string, string> = {
  "Silver": "Ag",
  "Arsenic": "As", 
  "Gold": "Au",
  "Aluminum": "Al",
  "Boron": "B",
  "Bismuth": "Bi",
  "Cobalt": "Co",
  "Diamond": "Diamond",
  "Cesium": "Cs",
  "Gallium": "Ga",
  "Helium": "He",
  "Germanium": "Ge",
  "Iodine": "I",
  "Lithium": "Li",
  "Manganese": "Mn",
  "Niobium": "Nb",
  "Phosphorus": "Phosphorus",
  "Lead": "Pb",
  "Zirconium": "Zr",
  "Tungsten": "W",
  "Uranium": "U",
  "Titanium": "Ti",
  "Talc": "Talc",
  "Strontium": "Sr",
  "Silicon": "Si",
  "Scandium": "Sc",
  "Sulfur": "S",
  "Rhodium": "Rh",
  "Rubidium": "Rb",
  "Platinum": "Pt",
  "Zinc": "Zn",
  "Vanadium": "V",
  "Thallium": "Tl",
  "Tellurium": "Te",
  "Tantalum": "Ta",
  "Tin": "Sn",
  "Selenium": "Se",
  "Ruthenium": "Rt",
  "Antimony": "Sb",
  "Rhenium": "Re",
  "REE": "REE",
  "Potash": "Potash (KCl)",
  "Palladium": "Pd",
  "Nickel": "Ni",
  "Molybdenum": "Mo",
  "Magnesium": "Mg",
  "Indium": "In",
  "Mercury": "Hg",
  "Graphite": "Graphite",
  "Garnet": "Garnet",
  "Iron": "Fe",
  "Copper": "Cu",
  "Chromium": "Cr",
  "Cadmium": "Cd",
  "Beryllium": "Be"
}

const transformFiltersToBackendPayload = (filters: FilterState) => {
  const mappedCommodities = filters.commodity.map(commodity =>
    COMMODITY_MAPPING[commodity] || commodity
  )

  // Convert dateFilter to new MiningHub date format
  const convertDateFilterToMiningHubFormat = () => {
    const dateFilter = filters.dateFilter || { preset: 'today' }
    
    if (dateFilter.preset === 'custom' && dateFilter.customRange) {
      // For custom ranges, use explicit date format
      const start = dateFilter.customRange.startDate
      const end = dateFilter.customRange.endDate || new Date()
      if (start) {
        const formatDate = (date: Date) => {
          return date.toISOString().split('T')[0] // YYYY-MM-DD format
        }
        return {
          kind: "explicit",
          value: {
            fromDate: formatDate(start),
            toDate: formatDate(end)
          }
        }
      }
    }
    
    // Convert preset to relative format
    switch (dateFilter.preset) {
      case 'today': 
        return { kind: "relative", value: "today" }
      case 'last-7-days': 
        return { kind: "relative", value: "week" }
      case 'last-30-days': 
        return { kind: "relative", value: "30-days" }
      case 'this-year': 
        return { kind: "relative", value: "year" }
      case 'all-time': 
        return { kind: "relative", value: "all" }
      default: 
        return { kind: "relative", value: "today" }
    }
  }

  // Transform to match the new FilterRequest structure expected by backend
  return {
    date: convertDateFilterToMiningHubFormat(),
    country: filters.country || "",
    state: filters.state || "",
    commodity: mappedCommodities,
    type: filters.type || [],
    free_text_search: filters['free-text-search'] || "",
    company: filters.company || "",
    project: filters.project || "",
    marketcap: filters.marketcap || { min: 0, max: 10000 },
    outstandingshares: filters.outstandingshares || { min: 0, max: 10000 }
  }
}

// Add request interceptor for logging
api.interceptors.request.use(
  (config) => {
    console.log('API Request:', config.method?.toUpperCase(), config.url)
    return config
  },
  (error) => {
    console.error('API Request Error:', error)
    return Promise.reject(error)
  }
)

// Add response interceptor for logging
api.interceptors.response.use(
  (response) => {
    return response
  },
  (error) => {
    console.error('API Response Error:', error.response?.data || error.message)
    
    // Enhanced CORS debugging
    if (error.message === 'Network Error' && !error.response) {
      console.error('🚨 CORS or Network Error detected. Check:')
      console.error('1. Backend server is running on port 8002')
      console.error('2. CORS is configured for', window.location.origin)
      console.error('3. Authentication cookies are being sent')
    }
    
    return Promise.reject(error)
  }
)

// Dashboard APIs - Using session-based approach
export const dashboardApi = {
  getStats: (): Promise<DashboardStats> =>
    api.get('/dashboard/stats').then(res => res.data.data),
  
  getArticlesBySession: (sessionId: string, page: number = 1, pageSize: number = 20): Promise<{
    data: Article[];
    pagination: {
      current_page: number;
      page_size: number;
      total_count: number;
      total_pages: number;
      has_next: boolean;
      has_prev: boolean;
    };
  }> => {
    console.log(`Fetching articles for session: ${sessionId}, page: ${page}, pageSize: ${pageSize}`)
    return api.get(`/articles/session/${sessionId}?page=${page}&page_size=${pageSize}`)
      .then(res => ({
        data: res.data.data || [],
        pagination: res.data.pagination || {
          current_page: 1,
          page_size: pageSize,
          total_count: 0,
          total_pages: 1,
          has_next: false,
          has_prev: false
        }
      }))
  },

  getArticleById: (articleId: string): Promise<Article> =>
    api.get(`/article/${articleId}`).then(res => res.data.article),
}

// News fetching APIs - Using correct backend endpoints
export const newsApi = {
  fetchArticles: (filters: FilterState): Promise<{ session_id: string; message: string }> => {
    const payload = transformFiltersToBackendPayload(filters)
    console.log('Fetching news with payload:', payload)
    return api.post('/fetch-news-session', payload).then(res => res.data)
      .catch(error => {
        // Handle rate limiting gracefully
        if (error.response?.status === 429) {
          const rateLimitData = error.response.data
          throw new Error(`Rate limit exceeded. Please wait ${rateLimitData.wait_time} seconds before trying again.`)
        }
        throw error
      })
  },
  
  getProgress: (sessionId: string): Promise<any> =>
    api.get(`/processing-status/${sessionId}`).then(res => res.data),
    
  getRateLimitStatus: (): Promise<any> =>
    api.get('/rate-limit/status').then(res => res.data),
}

// Article processing APIs - Using correct backend endpoints
export const articleApi = {
  expandDescription: (articleId: string): Promise<{ success: boolean; message: string }> =>
    api.post('/expand-article', { article_id: articleId }).then(res => res.data),
  
  summarizeArticle: (articleId: string): Promise<{ success: boolean; message: string }> =>
    api.post('/openai/process-article', { article_id: articleId }).then(res => res.data),
  
  pushToHubspot: (articleId: string): Promise<{ success: boolean; message: string }> =>
    api.post('/hubspot/push-article', { article_id: articleId }).then(res => res.data),
  
  // Batch processing
  processSelectedArticles: (articleIds: string[]): Promise<{ success: boolean; message: string; batch_session_id: string; article_count: number }> =>
    api.post('/batch/process-selected', { article_ids: articleIds }).then(res => res.data),
  
  getBatchStatus: (batchSessionId: string): Promise<any> =>
    api.get(`/batch/status/${batchSessionId}`).then(res => res.data),
}

// Auto Mode Filter Configuration APIs
export const autoModeApi = {
  getAutoFilters: (): Promise<FilterState> =>
    api.get('/configurations/auto-mode').then(res => res.data.data),
  
  saveAutoFilters: (filters: FilterState): Promise<{ success: boolean; message: string }> =>
    api.post('/configurations/auto-mode', filters).then(res => res.data),
}

// Prompt Template Management APIs
export const promptApi = {
  getAllPrompts: (): Promise<any[]> =>
    api.get('/prompts').then(res => res.data.data),
  
  getActivePrompt: (): Promise<any> =>
    api.get('/prompts/active').then(res => res.data.data),
  
  createPrompt: (data: { name: string; prompt_text: string; description?: string; function_schema?: any; set_as_active?: boolean }): Promise<{ success: boolean; data: any }> =>
    api.post('/prompts', data).then(res => res.data),
  
  updatePrompt: (id: number, data: { name?: string; prompt_text?: string; description?: string; function_schema?: any }): Promise<{ success: boolean; message: string }> =>
    api.put(`/prompts/${id}`, data).then(res => res.data),
  
  activatePrompt: (id: number): Promise<{ success: boolean; message: string }> =>
    api.post(`/prompts/${id}/activate`).then(res => res.data),
  
  deletePrompt: (id: number): Promise<{ success: boolean; message: string }> =>
    api.delete(`/prompts/${id}`).then(res => res.data),
  
  validateSchema: (schema: any): Promise<{ success: boolean; data: { valid: boolean; errors: string[]; warnings: string[] } }> =>
    api.post('/prompts/validate-schema', { schema }).then(res => res.data),
}

// Pin Board APIs
export const pinBoardApi = {
  // Quick Links
  getQuickLinks: (): Promise<{ success: boolean; data: any[] }> =>
    api.get('/pinboard/quick-links').then(res => res.data),
  
  createQuickLink: (data: { label: string; url: string; color: string; icon?: string }): Promise<{ success: boolean; message: string }> =>
    api.post('/pinboard/quick-links', data).then(res => res.data),
  
  updateQuickLink: (id: number, data: { label: string; url: string; color: string; icon?: string }): Promise<{ success: boolean; message: string }> =>
    api.put(`/pinboard/quick-links/${id}`, data).then(res => res.data),
  
  deleteQuickLink: (id: number): Promise<{ success: boolean; message: string }> =>
    api.delete(`/pinboard/quick-links/${id}`).then(res => res.data),
  

  // Widgets
  getWidgets: (): Promise<{ success: boolean; data: any[] }> =>
    api.get('/pinboard/widgets').then(res => res.data),
  
  createWidget: (data: { title: string; type: string; content: string; size: string; grid_row?: number; grid_col?: number }): Promise<{ success: boolean; message: string }> =>
    api.post('/pinboard/widgets', data).then(res => res.data),
  
  deleteWidget: (id: number): Promise<{ success: boolean; message: string }> =>
    api.delete(`/pinboard/widgets/${id}`).then(res => res.data),
}

export default api 